import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Users, PowerOff, Ban, CheckCircle, UserCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Personale() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [activeShifts, setActiveShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/users"),
      api.get("/auth/roles"),
      api.get("/shifts/active"),
    ]).then(([u, r, s]) => {
      setUsers(u.data);
      setRoles(r.data);
      setActiveShifts(s.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirettore = user?.role === "Proprietario";
  const isApprover = user?.role === "Proprietario" || user?.role === "Supervisore";

  const updateRole = async (userId, role) => {
    try {
      await api.post("/users/update-role", { user_id: userId, role });
      toast.success("Ruolo aggiornato");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const forceStop = async (userId, name) => {
    if (!window.confirm(`Forzare l'uscita dal turno di ${name}?`)) return;
    try {
      await api.post("/shifts/force-stop", { user_id: userId });
      toast.success("Turno chiuso forzatamente");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const approveUser = async (u, approved) => {
    if (!approved && !window.confirm(`Rifiutare la richiesta di ${u.display_name || u.username}? L'utente non potrà accedere finché non lo approvi.`)) return;
    try {
      await api.post("/users/approve", { user_id: u.id, approved });
      toast.success(approved ? `${u.display_name || u.username} approvato · può ora accedere` : "Approvazione revocata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleBan = async (u) => {    if (u.banned) {
      if (!window.confirm(`Sbloccare l'accesso di ${u.display_name || u.username}?`)) return;
      try {
        await api.post("/users/ban", { user_id: u.id, banned: false });
        toast.success("Accesso ripristinato");
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    } else {
      const reason = window.prompt(`Motivo del blocco di ${u.display_name || u.username}? (opzionale)`, "");
      if (reason === null) return;
      try {
        await api.post("/users/ban", { user_id: u.id, banned: true, reason: reason || null });
        toast.success(`${u.display_name || u.username} bloccato · non potrà più accedere`);
        load();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }
  };

  const activeByUser = Object.fromEntries(activeShifts.map((s) => [s.user_id, s]));

  return (
    <div className="w-full">
      <PageHeader
        icon={Users}
        title="Personale"
        subtitle={isDirettore ? "Gestione ruoli · Controllo turni attivi." : isApprover ? "Approvazione richieste di accesso." : "Elenco operatori registrati."}
      />

      {!isDirettore && !isApprover && (
        <div className="ot-panel p-4 mb-6 border-amber-500/30 text-amber-400 text-sm">
          Solo il Direttore (Proprietario) può modificare ruoli e forzare la chiusura dei turni.
        </div>
      )}
      {!isDirettore && isApprover && (
        <div className="ot-panel p-4 mb-6 border-blue-500/30 text-blue-300 text-sm">
          Come Supervisore puoi approvare o rifiutare le richieste di accesso al sito. Le altre operazioni sono riservate al Proprietario.
        </div>
      )}

      <div className="ot-panel-elev p-6 ot-fade-up">
        {loading ? (
          <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
        ) : (
          <>
            {users.some((u) => !u.approved) && (
              <div className="mb-4 flex items-center gap-2 ot-panel p-3 border-amber-500/30 bg-amber-500/5">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm">
                  {users.filter((u) => !u.approved).length} richiest{users.filter((u) => !u.approved).length === 1 ? "a" : "e"} in attesa di approvazione
                </span>
              </div>
            )}
            <div className="space-y-2">
              {[...users].sort((a, b) => (a.approved === b.approved ? 0 : a.approved ? 1 : -1)).map((u) => {
              const initials = (u.display_name || u.username).split(/[\s._-]+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
              const activeShift = activeByUser[u.id];
              return (
                <div key={u.id} className={`ot-panel p-4 flex items-center gap-4 ${u.banned ? "border-red-500/40 bg-red-500/[0.03]" : !u.approved ? "border-amber-500/40 bg-amber-500/[0.03]" : ""}`} data-testid={`user-row-${u.username}`}>
                  <div className={`w-10 h-10 rounded-md border flex items-center justify-center ot-heading font-bold text-sm ${u.banned ? "border-red-500/40 bg-red-500/10 text-red-400" : !u.approved ? "border-amber-500/40 bg-amber-500/10 text-amber-400" : "border-blue-400/40 bg-blue-400/10 text-blue-400"}`}>
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="ot-heading font-bold text-slate-100 uppercase">{u.display_name || u.username}</div>
                      {!u.approved && !u.banned && (
                        <span className="ot-mono text-[0.6rem] text-amber-400 tracking-widest border border-amber-500/40 rounded px-1.5 py-0.5">
                          <Clock className="w-2.5 h-2.5 inline mr-1" />IN ATTESA
                        </span>
                      )}
                      {u.banned && (
                        <span className="ot-mono text-[0.6rem] text-red-400 tracking-widest border border-red-500/40 rounded px-1.5 py-0.5">
                          <Ban className="w-2.5 h-2.5 inline mr-1" />BLOCCATO
                        </span>
                      )}
                    </div>
                    <div className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">@{u.username}</div>
                    {u.date_of_birth && (
                      <div className="ot-mono text-[0.7rem] text-blue-400/80 tracking-widest mt-0.5">
                        NATO IL {new Date(u.date_of_birth).toLocaleDateString("it-IT")}
                      </div>
                    )}
                    {u.banned && u.ban_reason && (
                      <div className="ot-mono text-[0.7rem] text-red-400/80 tracking-widest mt-0.5">
                        MOTIVO: {u.ban_reason}
                      </div>
                    )}
                    {activeShift && (
                      <div className="mt-1 flex items-center gap-2 text-[0.7rem] ot-mono">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 ot-blink" />
                        <span className="text-emerald-400 tracking-widest">IN SERVIZIO</span>
                        <span className="text-slate-500">dal {formatDate(activeShift.start_at)}</span>
                      </div>
                    )}
                  </div>
                  {isDirettore || (isApprover && !u.approved) ? (
                    <>
                      {isApprover && !u.approved && (
                        <>
                          <button
                            data-testid={`approve-btn-${u.username}`}
                            onClick={() => approveUser(u, true)}
                            className="ot-btn-ghost !py-2 !px-3 flex items-center gap-1.5 !text-emerald-400 !border-emerald-500/40 hover:!bg-emerald-500/10"
                            title="Approva l'accesso al sito"
                          >
                            <UserCheck className="w-3.5 h-3.5" />
                            Approva
                          </button>
                          <button
                            data-testid={`reject-btn-${u.username}`}
                            onClick={() => approveUser(u, false)}
                            className="ot-btn-ghost !py-2 !px-3 flex items-center gap-1.5 !text-red-400 !border-red-500/40 hover:!bg-red-500/10"
                            title="Rifiuta"
                          >
                            <Ban className="w-3.5 h-3.5" />
                            Rifiuta
                          </button>
                        </>
                      )}
                      {isDirettore && u.approved && (<>
                      <select
                        data-testid={`role-select-${u.username}`}
                        className="ot-input max-w-xs"
                        value={u.role || "Meccanico in prova"}
                        onChange={(e) => updateRole(u.id, e.target.value)}
                        disabled={u.banned}
                      >
                        {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {activeShift && (
                        <button
                          data-testid={`force-stop-${u.username}`}
                          onClick={() => forceStop(u.id, u.display_name || u.username)}
                          className="ot-btn-danger !py-2 !px-3 flex items-center gap-1.5"
                          title="Forza uscita dal turno"
                        >
                          <PowerOff className="w-3.5 h-3.5" />
                          Chiudi turno
                        </button>
                      )}
                      {u.id !== user?.id && (
                        <button
                          data-testid={`ban-btn-${u.username}`}
                          onClick={() => toggleBan(u)}
                          className={`ot-btn-ghost !py-2 !px-3 flex items-center gap-1.5 ${u.banned ? "!text-emerald-400 !border-emerald-500/40 hover:!bg-emerald-500/10" : "!text-red-400 !border-red-500/40 hover:!bg-red-500/10"}`}
                          title={u.banned ? "Sblocca accesso" : "Blocca accesso"}
                        >
                          {u.banned ? <><CheckCircle className="w-3.5 h-3.5" /> Sblocca</> : <><Ban className="w-3.5 h-3.5" /> Blocca</>}
                        </button>
                      )}
                      </>)}
                    </>
                  ) : (
                    <div className="ot-mono text-xs text-blue-400/70 tracking-widest">{u.role}</div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
